<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { replaceUnderscoreWithSpace } from './helper/filterHelper.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  appliedFilters: { type: Array, default: () => [] },
  maxVisibleFilters: { type: Number, default: 2 },
  clearButtonLabel: { type: String, default: '' },
  moreFiltersLabel: { type: String, default: '' },
  showClearButton: { type: Boolean, default: true },
});

const emit = defineEmits(['clearFilters', 'openFilter']);

const { t } = useI18n();

const shouldCapitalizeFirstLetter = key => {
  const lowercaseKeys = ['email'];
  return !lowercaseKeys.includes(key);
};

const formatAttributeName = attributeKey => {
  // Use translation for labels
  if (attributeKey === 'labels') {
    return t('FILTER.ATTRIBUTES.LABELS');
  }
  // Try to get translation, fallback to formatted key
  const translationKey = `FILTER.ATTRIBUTES.${attributeKey.toUpperCase()}`;
  const translated = t(translationKey);
  if (translated !== translationKey) {
    return translated;
  }
  return replaceUnderscoreWithSpace(attributeKey);
};

const formatOperatorLabel = operator => {
  const operators = {
    equal_to: '=',
    not_equal_to: '≠',
    contains: 'contains',
    does_not_contain: 'does not contain',
    is_present: 'is present',
    is_not_present: 'is not present',
    is_greater_than: 'is greater than',
    is_less_than: 'is less than',
    days_before: 'days before',
  };
  return operators[operator] || replaceUnderscoreWithSpace(operator);
};

const formatFilterValue = (value, attributeKey) => {
  if (!value) return '';
  
  // Normalizar para array se necessário
  let valuesArray = [];
  
  if (Array.isArray(value)) {
    valuesArray = value;
  } else if (typeof value === 'object') {
    // Se for um objeto único, tentar extrair valores
    if (value.name) {
      valuesArray = [value];
    } else if (value.values && Array.isArray(value.values)) {
      valuesArray = value.values;
    } else {
      // Tentar converter objeto em array de valores
      valuesArray = Object.values(value).filter(v => v != null);
    }
  } else {
    valuesArray = [value];
  }
  
  // Se o array contém objetos com propriedade 'name', extrair os nomes
  if (valuesArray.length > 0 && typeof valuesArray[0] === 'object' && valuesArray[0].name) {
    return valuesArray.map(item => item.name || item.id || item.title || item).filter(Boolean).join(', ');
  }
  
  // Para arrays de strings ou valores simples
  return valuesArray
    .map(item => {
      if (typeof item === 'object' && item.name) {
        return item.name;
      }
      if (typeof item === 'object' && item.title) {
        return item.title;
      }
      return String(item);
    })
    .filter(Boolean)
    .join(', ');
};
</script>

<template>
  <div class="flex flex-wrap items-center w-full gap-2 mx-auto">
    <template v-for="(filter, index) in appliedFilters" :key="index">
      <div
        v-if="index < maxVisibleFilters"
        class="inline-flex items-center gap-2 h-7"
      >
        <div
          class="flex items-center h-full min-w-0 gap-1 px-2 py-1 text-xs border rounded-lg hover:bg-n-solid-2 max-w-72 border-n-weak hover:cursor-pointer"
          @click="emit('openFilter')"
        >
          <span
            class="lowercase whitespace-nowrap first-letter:capitalize text-n-slate-12"
          >
            {{ formatAttributeName(filter.attributeKey) }}
          </span>
          <span class="px-1 text-xs text-n-slate-10 whitespace-nowrap">
            {{ formatOperatorLabel(filter.filterOperator) }}
          </span>
          <span
            v-if="filter.values"
            class="lowercase truncate text-n-slate-12"
            :class="{
              'first-letter:capitalize': shouldCapitalizeFirstLetter(
                filter.attributeKey
              ),
            }"
          >
            {{ formatFilterValue(filter.values, filter.attributeKey) }}
          </span>
        </div>
        <template
          v-if="
            index < maxVisibleFilters - 1 && index < appliedFilters.length - 1
          "
        >
          <span
            class="content-center h-full px-1 text-xs font-medium uppercase rounded-lg text-n-slate-10"
          >
            {{ filter.queryOperator }}
          </span>
        </template>
      </div>
    </template>
    <div
      v-if="appliedFilters.length > maxVisibleFilters"
      class="inline-flex items-center content-center px-1 text-xs rounded-lg text-n-slate-10 hover:text-n-slate-11 h-7 hover:cursor-pointer"
      @click="emit('openFilter')"
    >
      {{ moreFiltersLabel }}
    </div>
    <div v-if="showClearButton" class="w-px h-3 rounded-lg bg-n-strong" />
    <Button
      v-if="showClearButton"
      :label="clearButtonLabel"
      size="xs"
      class="!px-1"
      variant="ghost"
      @click="emit('clearFilters')"
    />
  </div>
</template>
